import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PortfolioCaseComponent from '../Portfolio/PortfolioCase';
import ShowcaseGrid from '../Showcase/ShowcaseGrid';
import { portfolioCases } from '../../data/portfolioCases';

type PaneIndex = 0 | 1 | 2;

const PATH_TO_PANE: Record<string, PaneIndex> = {
  '/': 1,
  '/case-studies': 0,
  '/showcase': 2,
};

const PANE_TO_PATH: Record<PaneIndex, string> = {
  0: '/case-studies',
  1: '/',
  2: '/showcase',
};

const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

const SplitDeck: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const deckRef = useRef<HTMLDivElement>(null);
  const activePaneRef = useRef<PaneIndex>(1);
  const [activePane, setActivePane] = useState<PaneIndex>(
    PATH_TO_PANE[location.pathname] ?? 1
  );

  useEffect(() => {
    document.documentElement.classList.add('black');
    return () => {
      document.documentElement.classList.remove('black');
    };
  }, []);

  const isMobile = () =>
    typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches;

  const scrollToPane = useCallback(
    (pane: PaneIndex, behavior: ScrollBehavior = 'smooth') => {
      const deck = deckRef.current;
      if (!deck) return;
      if (isMobile()) {
        // Mobile panes are min-height: 100vh but can be taller (case
        // studies stacks four tiles), so `pane * innerHeight` would
        // land mid-pane. Read the target pane's actual offsetTop.
        const panes = deck.querySelectorAll<HTMLElement>('.split-deck-pane');
        const target = panes[pane];
        if (target) {
          deck.scrollTo({ top: target.offsetTop, behavior });
        }
      } else {
        // Desktop: 70vw hero between 85vw edge panes; the snap point
        // for pane i (snap-align: center for hero, start/end for
        // edges) sits at scrollLeft = i * 70vw.
        deck.scrollTo({ left: pane * 0.7 * window.innerWidth, behavior });
      }
    },
    []
  );

  // On route change, jump to the matching pane.
  useEffect(() => {
    const target = PATH_TO_PANE[location.pathname] ?? 1;
    if (target !== activePaneRef.current) {
      activePaneRef.current = target;
      setActivePane(target);
      scrollToPane(target, 'auto');
    } else {
      // Initial mount: ensure we land on the right pane.
      scrollToPane(target, 'auto');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Re-snap on resize so the active pane stays centered.
  useEffect(() => {
    const onResize = () => scrollToPane(activePaneRef.current, 'auto');
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [scrollToPane]);

  // Track which pane is most visible and sync the URL accordingly.
  useEffect(() => {
    const deck = deckRef.current;
    if (!deck) return;
    const panes = deck.querySelectorAll<HTMLElement>('.split-deck-pane');
    if (panes.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let bestIdx = -1;
        let bestRatio = 0;
        entries.forEach((entry) => {
          if (entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            bestIdx = Array.from(panes).indexOf(entry.target as HTMLElement);
          }
        });
        if (bestIdx < 0 || bestRatio < 0.6) return;
        const pane = bestIdx as PaneIndex;
        if (pane !== activePaneRef.current) {
          activePaneRef.current = pane;
          setActivePane(pane);
          const path = PANE_TO_PATH[pane];
          if (path !== window.location.pathname) {
            window.history.replaceState(null, '', path);
          }
        }
      },
      {
        root: deck,
        threshold: [0.5, 0.6, 0.75, 0.9],
      }
    );

    panes.forEach((p) => observer.observe(p));
    return () => observer.disconnect();
  }, []);

  // Arrow key navigation between panes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      const mobile = isMobile();
      const forward = mobile ? 'ArrowDown' : 'ArrowRight';
      const back = mobile ? 'ArrowUp' : 'ArrowLeft';
      if (e.key === forward && activePaneRef.current < 2) {
        e.preventDefault();
        scrollToPane((activePaneRef.current + 1) as PaneIndex);
      } else if (e.key === back && activePaneRef.current > 0) {
        e.preventDefault();
        scrollToPane((activePaneRef.current - 1) as PaneIndex);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [scrollToPane]);

  const handleCaseClick = (caseId: string) => {
    const caseData = portfolioCases.find((c) => c.id === caseId);
    if (caseData) navigate(`/${caseData.slug}`);
  };

  const copyRightYear = new Date().getFullYear();

  return (
    <div className="split-deck-shell">
      <div className="split-deck" ref={deckRef} aria-live="polite">
        <section
          className="split-deck-pane pane-case-studies"
          aria-label="Case studies"
        >
          <div className="pane-inner">
            <header className="pane-header pane-header-left">
              <p className="pane-eyebrow">Case Studies</p>
              <h1 className="pane-title">
                Stories of transformation.
              </h1>
              <p className="pane-lede">
                The long view of how iconic brands moved from one chapter to the next.
              </p>
            </header>

            <div className="case-studies-list">
              {portfolioCases.map((caseData) => (
                <PortfolioCaseComponent
                  key={caseData.id}
                  caseData={{ ...caseData, isBig: true }}
                  onClick={handleCaseClick}
                />
              ))}
            </div>
          </div>
        </section>

        <section
          className="split-deck-pane pane-hero"
          aria-label="Greg Johnson"
        >
          <div className="pane-inner pane-hero-inner">
            <svg
              className="pane-hero-logo"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 442.5 442.5"
              aria-hidden="true"
            >
              <g>
                <path
                  fill="#fff"
                  d="M271.25,0V50H392.5V222.7a168.69,168.69,0,0,1-49.73,120.07A171.68,171.68,0,0,1,321.25,361v58.2A219.79,219.79,0,0,0,442.5,222.7V0Z"
                />
                <path
                  fill="#fff"
                  d="M171.25,292.5h50v100A172.67,172.67,0,0,1,50,219.8,169.79,169.79,0,0,1,219.8,50h1.45V0H219.8C98.41,0,0,98.41,0,219.8H0C0,342.31,98.93,441.7,221.25,442.48v0h50v-200h-100Z"
                />
              </g>
            </svg>

            <h1 className="pane-hero-name">Greg Johnson</h1>
            <p className="pane-hero-tagline">I make complex ideas real.</p>
            <p className="pane-hero-sub">
              The strategy that makes a business legible, the experiences that bring it to life. Usually both, usually
              on the same project.
            </p>

            <div className="pane-hero-affordances">
              <button
                className="pane-affordance pane-affordance-left"
                onClick={() => scrollToPane(0)}
                aria-label="Go to case studies"
              >
                <span className="pane-affordance-arrow">←</span>
                <span className="pane-affordance-label">
                  <span className="pane-affordance-kicker">Case Studies</span>
                  <span className="pane-affordance-line">
                    Businesses I’ve helped transform.
                  </span>
                </span>
              </button>
              <button
                className="pane-affordance pane-affordance-right"
                onClick={() => scrollToPane(2)}
                aria-label="Go to showcase"
              >
                <span className="pane-affordance-label">
                  <span className="pane-affordance-kicker">Showcase</span>
                  <span className="pane-affordance-line">
                    The work I’ve produced.
                  </span>
                </span>
                <span className="pane-affordance-arrow">→</span>
              </button>
            </div>

            <footer className="pane-hero-footer">
              <a href="https://www.linkedin.com/in/gregjohnson">LinkedIn</a>
              <span>© {copyRightYear} Greg Johnson</span>
              <a href="mailto:greg@gregjohnson.info">Contact</a>
            </footer>
          </div>
        </section>

        <section
          className="split-deck-pane pane-showcase"
          aria-label="Showcase"
        >
          <div className="pane-inner">
            <ShowcaseGrid />
          </div>
        </section>

        {/* Chevrons live inside the scrolling deck so they ride along with
            the pane edges they're anchored to. */}
        <button
          type="button"
          className={`split-deck-chevron split-deck-chevron-left ${
            activePane === 0 ? 'is-flipped' : ''
          }`}
          aria-label={
            activePane === 0 ? 'Back to home' : 'Case studies'
          }
          onClick={() =>
            scrollToPane(activePane === 0 ? 1 : 0)
          }
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 6l-6 6 6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <button
          type="button"
          className={`split-deck-chevron split-deck-chevron-right ${
            activePane === 2 ? 'is-flipped' : ''
          }`}
          aria-label={
            activePane === 2 ? 'Back to home' : 'Showcase'
          }
          onClick={() =>
            scrollToPane(activePane === 2 ? 1 : 2)
          }
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M9 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SplitDeck;
